import * as S from "@radix-ui/react-select";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { ChevronDown, Check } from "@/icons";

// Select — custom, fully-styled dropdown on Radix Select (keyboard nav,
// typeahead, ARIA, mobile-friendly) so it matches the app instead of the OS
// native picker.
export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  value?: string;
  onValueChange?: (v: string) => void;
  defaultValue?: string;
  options: SelectOption[];
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  contentClassName?: string;
  "aria-label"?: string;
}

export function Select({
  value,
  onValueChange,
  defaultValue,
  options,
  placeholder,
  invalid,
  disabled,
  id,
  className,
  contentClassName,
  ...aria
}: SelectProps) {
  const { t } = useTranslation("ui");
  return (
    <S.Root value={value} onValueChange={onValueChange} defaultValue={defaultValue} disabled={disabled}>
      <S.Trigger
        id={id}
        aria-invalid={invalid || undefined}
        aria-label={aria["aria-label"]}
        className={cn(
          "inline-flex h-11 w-full items-center justify-between gap-2 rounded-xl border bg-input px-3.5 text-sm text-fg transition-colors",
          // Radix renders the selected value in a child <span>; keep it on one
          // line and ellipsize (never wrap to two lines) when the trigger is narrow.
          "[&>span]:min-w-0 [&>span]:truncate",
          "data-[placeholder]:text-fg-subtle",
          "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/45",
          "disabled:cursor-not-allowed disabled:opacity-50",
          invalid ? "border-danger" : "border-border",
          className,
        )}
      >
        <S.Value placeholder={placeholder ?? t("select.placeholder")} />
        <S.Icon asChild>
          <ChevronDown className="h-4 w-4 shrink-0 text-fg-muted" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className={cn(
            "z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-border bg-surface p-1 shadow-elevated",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
            contentClassName,
          )}
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className={cn(
                  "relative flex cursor-pointer select-none items-center rounded-lg py-2 pl-8 pr-3 text-sm text-fg outline-none transition-colors",
                  "data-[highlighted]:bg-surface-2 data-[state=checked]:font-medium",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                )}
              >
                <span className="absolute left-2 inline-flex">
                  <S.ItemIndicator>
                    <Check className="h-4 w-4 text-primary" strokeWidth={3} />
                  </S.ItemIndicator>
                </span>
                <S.ItemText>{o.label}</S.ItemText>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}
